import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { MultiSelect } from "@/components/ui/multi-select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChipOpcion, Icon } from "@/components/ds";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, ApiError } from "@/lib/api";
import { formatNombreArea, ordenarAreas } from "@/lib/area";
import type { AreaSummary } from "@/types/area";
import type { Maquinaria } from "@/types/maquinaria";

/**
 * Tipos que ofrece el prototipo como atajo. NO es un catálogo cerrado: la
 * columna `tipo` es texto libre en la API y hay inventarios cargados con
 * valores fuera de esta lista, así que el valor actual de la maquinaria se
 * agrega como un chip más si no está acá.
 */
const TIPOS_SUGERIDOS = [
  "Excavadora",
  "Retroexcavadora",
  "Cargador",
  "Bulldozer",
  "Motoniveladora",
  "Compactadora",
  "Grúa horquilla",
  "Camión Tolva",
];

/** Sugerencias del `datalist` de marca — se puede escribir cualquier otra. */
const MARCAS_SUGERIDAS = [
  "Caterpillar",
  "Komatsu",
  "Volvo",
  "JCB",
  "Bomag",
  "Toyota",
  "Hyundai",
  "Doosan",
];

/**
 * Código sugerido a partir del tipo: tres letras + correlativo (`EXC-001`),
 * tal como el prototipo. Se normaliza a NFD y se descartan los diacríticos
 * para que "Grúa horquilla" dé `GRU` y no un código con tilde.
 *
 * `usados` DEBE incluir los códigos de la papelera: `maquinarias` usa
 * SoftDeletes y la regla `unique` de la API consulta la tabla cruda, así que
 * una máquina en la papelera sigue ocupando su código.
 */
function codigoSugerido(tipo: string, usados: string[]): string {
  const raiz =
    tipo
      .toUpperCase()
      .normalize("NFD")
      .replace(/[^A-Z]/g, "")
      .slice(0, 3) || "MAQ";
  const tomados = new Set(usados.map((c) => c.trim().toUpperCase()));
  let n = 1;
  while (tomados.has(`${raiz}-${String(n).padStart(3, "0")}`)) n++;
  return `${raiz}-${String(n).padStart(3, "0")}`;
}

export interface MaquinariaFormProps {
  mode: "create" | "edit";
  maquinaria?: Maquinaria;
  /** Se invoca al terminar de guardar con la maquinaria creada/actualizada. */
  onSaved: (maquinaria: Maquinaria) => void;
  /**
   * Render prop para las acciones del formulario (botón Guardar). Permite que
   * cada consumidor (drawer de alta vs. tab Información de la Ficha) decida su
   * propio contenedor/estilo, manteniendo el submit dentro del `<form>`.
   * Recibe también `resumen`, el texto que el pie del drawer usa como pista de
   * lo que falta. Mismo patrón que `VehiculoForm`/`TrabajadorForm`.
   */
  renderFooter: (state: { isSubmitting: boolean; resumen: string }) => ReactNode;
  /** Override del className del `<form>` — por defecto, layout simple en columna. */
  formClassName?: string;
  /**
   * Autofoco en el primer campo. Por defecto `true` — comportamiento del
   * drawer de alta. La Ficha a página completa lo desactiva para no robar el
   * foco (ni forzar scroll) al montar el tab Información.
   */
  autoFocusFirst?: boolean;
  /**
   * Cómo se ordenan los campos. Los campos, su validación y el payload se
   * declaran UNA sola vez; sólo cambia la disposición, para no terminar
   * manteniendo las mismas reglas en dos componentes.
   *
   * - `dialogo` (por defecto): el drawer de alta, en bloques separados por
   *   divisores.
   * - `secciones`: tarjetas independientes para la Ficha.
   *
   * `activo` no pertenece a ninguno de los dos: lo maneja la Ficha con
   * `PATCH /maquinarias/{id}/estado`.
   */
  layout?: "dialogo" | "secciones";
  /**
   * Códigos y series ya cargados en la empresa — inventario Y papelera. Sirven
   * para sugerir el próximo código libre y para avisar de un duplicado antes de
   * mandar el formulario. Se omiten en la Ficha, donde el resto del inventario
   * no está a mano: sin la lista simplemente no se sugiere ni se avisa.
   */
  codigosUsados?: string[];
  seriesUsadas?: string[];
  /**
   * Cuántos tipos de documento le exige la empresa a una maquinaria. Sólo
   * alimenta la pista del pie ("sus N documentos quedan pendientes"); si es 0
   * —empresa sin catálogo configurado— no se promete nada.
   */
  documentosTotal?: number;
}

/**
 * Cuerpo de campos del formulario de maquinaria. Lo comparten el drawer de
 * "Nueva maquinaria" y el tab Información de la Ficha; la validación y el
 * contrato con la API son los mismos en los dos.
 */
export function MaquinariaForm({
  mode,
  maquinaria,
  onSaved,
  renderFooter,
  formClassName,
  autoFocusFirst = true,
  layout = "dialogo",
  codigosUsados = [],
  seriesUsadas = [],
  documentosTotal = 0,
}: MaquinariaFormProps) {
  const uid = useId();
  const [nombre, setNombre] = useState(maquinaria?.nombre ?? "");
  const [codigo, setCodigo] = useState(maquinaria?.codigo ?? "");
  const [tipo, setTipo] = useState(maquinaria?.tipo ?? "");
  const [marca, setMarca] = useState(maquinaria?.marca ?? "");
  const [modelo, setModelo] = useState(maquinaria?.modelo ?? "");
  const [anio, setAnio] = useState<string>(maquinaria?.anio != null ? String(maquinaria.anio) : "");
  const [serie, setSerie] = useState(maquinaria?.serie ?? "");
  const [areas, setAreas] = useState<AreaSummary[]>([]);
  const [areasIds, setAreasIds] = useState<string[]>(
    maquinaria?.areas?.map((a) => String(a.id)) ?? [],
  );
  const [isLoadingAreas, setIsLoadingAreas] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Mientras es `true` el código lo maneja la sugerencia y se recalcula al
   * cambiar el tipo. Se apaga en cuanto el usuario escribe el suyo, y arranca
   * apagado al editar: el código de una máquina ya cargada no se toca solo.
   */
  const [codigoAuto, setCodigoAuto] = useState(mode === "create" && !maquinaria?.codigo);

  const esSecciones = layout === "secciones";

  const areasOrdenadas = useMemo(() => ordenarAreas(areas), [areas]);
  const areasOptions = useMemo(
    () => areasOrdenadas.map((a) => ({ value: String(a.id), label: formatNombreArea(a) })),
    [areasOrdenadas],
  );

  /** Los sugeridos más el valor actual, si la maquinaria trae uno fuera de la lista. */
  const tiposChips = useMemo(() => {
    const actual = tipo.trim();
    if (actual && !TIPOS_SUGERIDOS.includes(actual)) return [...TIPOS_SUGERIDOS, actual];
    return TIPOS_SUGERIDOS;
  }, [tipo]);

  /** El propio código/serie no cuenta como duplicado de sí mismo al editar. */
  const codigosAjenos = useMemo(() => {
    const propio = (maquinaria?.codigo ?? "").trim().toUpperCase();
    return codigosUsados
      .map((c) => c.trim().toUpperCase())
      .filter((c) => c && c !== propio);
  }, [codigosUsados, maquinaria?.codigo]);

  const seriesAjenas = useMemo(() => {
    const propia = (maquinaria?.serie ?? "").trim().toUpperCase();
    return seriesUsadas
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s && s !== propia);
  }, [seriesUsadas, maquinaria?.serie]);

  const codigoNormalizado = codigo.trim().toUpperCase();
  const dupCodigo = codigoNormalizado !== "" && codigosAjenos.includes(codigoNormalizado);
  const serieNormalizada = serie.trim().toUpperCase();
  const dupSerie = serieNormalizada !== "" && seriesAjenas.includes(serieNormalizada);

  /** Elegir un tipo también refresca el código mientras siga siendo automático. */
  const elegirTipo = (valor: string) => {
    const siguiente = tipo === valor ? "" : valor;
    setTipo(siguiente);
    if (codigoAuto) {
      setCodigo(siguiente ? codigoSugerido(siguiente, codigosAjenos) : "");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ data: AreaSummary[] }>("/areas?all=1");
        if (!cancelled) setAreas(res.data);
      } catch {
        toast.error("No se pudieron cargar las áreas.");
      } finally {
        if (!cancelled) setIsLoadingAreas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Qué falta para poder guardar. El pie del drawer lo muestra como pista en
   * vez de deshabilitar el botón en silencio: el usuario ve qué le falta antes
   * de intentar. Mismo criterio que el prototipo.
   */
  const resumen = useMemo(() => {
    if (dupCodigo) return "Ese código ya está en el inventario.";

    const falta = [
      !nombre.trim() && "el nombre",
      !tipo.trim() && "el tipo",
      !marca.trim() && "la marca",
      !modelo.trim() && "el modelo",
    ].filter(Boolean) as string[];

    if (falta.length > 0) {
      const lista =
        falta.length > 1
          ? `${falta.slice(0, -1).join(", ")} y ${falta[falta.length - 1]}`
          : falta[0];
      return `Falta ${lista}.`;
    }

    if (dupSerie) return "Ese número de serie ya está registrado en otra máquina.";

    if (mode === "create" && documentosTotal > 0) {
      return documentosTotal === 1
        ? "Su documento queda pendiente de subir."
        : `Sus ${documentosTotal} documentos quedan pendientes de subir.`;
    }

    return areasIds.length > 0
      ? "Queda disponible para los checklists de las áreas elegidas."
      : "Sin área queda visible para toda la empresa al hacer un checklist.";
  }, [dupCodigo, dupSerie, nombre, tipo, marca, modelo, mode, documentosTotal, areasIds]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) {
      toast.error("El nombre es requerido.");
      return;
    }
    if (!tipo.trim()) {
      toast.error("El tipo es requerido.");
      return;
    }
    if (!marca.trim()) {
      toast.error("La marca es requerida.");
      return;
    }
    if (!modelo.trim()) {
      toast.error("El modelo es requerido.");
      return;
    }
    // El código único lo valida también la API (`MaquinariaRequest`); acá se
    // corta antes para no gastar un viaje que ya sabemos que vuelve en 422.
    if (dupCodigo) {
      toast.error("Ya existe una maquinaria con ese código en tu empresa.");
      return;
    }
    const anioNum = anio.trim() ? Number(anio) : null;
    if (anio.trim() && (!Number.isInteger(anioNum) || anioNum! < 1900 || anioNum! > 2100)) {
      toast.error("El año debe estar entre 1900 y 2100.");
      return;
    }

    const payload = {
      nombre: nombre.trim(),
      codigo: codigo.trim() ? codigo.trim().toUpperCase() : null,
      tipo: tipo.trim(),
      marca: marca.trim(),
      modelo: modelo.trim(),
      anio: anioNum,
      serie: serie.trim() || null,
      // `activo` NO viaja nunca desde el formulario. En la Ficha lo maneja el
      // interruptor inmediato (`PATCH /maquinarias/{id}/estado`) y mandarlo acá
      // revertiría un activar/desactivar recién hecho. En el alta una máquina
      // nace en operación y punto: la API lo da por sentado con `sometimes` en
      // la regla, `default(true)` en la columna y `$attributes` en el modelo.
      areas_ids: areasIds.map(Number),
    };

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const res = await api.post<{ data: Maquinaria }>("/maquinarias", payload);
        toast.success("Maquinaria creada.");
        onSaved(res.data);
      } else if (maquinaria) {
        const res = await api.put<{ data: Maquinaria }>(`/maquinarias/${maquinaria.id}`, payload);
        toast.success("Maquinaria actualizada.");
        onSaved(res.data);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
      } else {
        toast.error("No se pudo guardar la maquinaria.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Campos ────────────────────────────────────────────────────────────────
  // Se declaran una vez y cada layout los ordena.

  const campoNombre = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-nombre`}>
        Nombre en terreno <span className="text-destructive">*</span>
      </Label>
      <Input
        id={`${uid}-nombre`}
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        placeholder="Excavadora hidráulica"
        disabled={isSubmitting}
        autoFocus={autoFocusFirst}
        required
      />
    </div>
  );

  const campoCodigo = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-codigo`}>Código interno</Label>
      <Input
        id={`${uid}-codigo`}
        value={codigo}
        onChange={(e) => {
          setCodigo(e.target.value.toUpperCase().slice(0, 50));
          setCodigoAuto(false);
        }}
        placeholder="EXC-003"
        disabled={isSubmitting}
        className="fc-num"
        // Rojo y `aria-invalid`, NO el ámbar del prototipo: acá el duplicado
        // BLOQUEA el guardado (la API exige `codigo` único por empresa), así
        // que decir "atención" cuando en realidad es "no se puede guardar"
        // sería mentir. El ámbar queda para la serie, que sólo avisa. Además
        // `Input` ya trae `aria-invalid:border-destructive`, que por
        // especificidad le gana a cualquier `border-*` que le pongamos acá.
        aria-invalid={dupCodigo}
      />
      {dupCodigo ? (
        <p className="text-destructive flex items-center gap-1.5 text-[11.5px]">
          <Icon name="error" size={15} />
          Ese código ya está en el inventario.
        </p>
      ) : codigoAuto && codigo ? (
        <p className="text-soft text-[11.5px]">Sugerido según el tipo · puedes cambiarlo</p>
      ) : null}
    </div>
  );

  const campoTipoChips = (
    <div className="space-y-1.5">
      <Label>
        Tipo de maquinaria <span className="text-destructive">*</span>
      </Label>
      <div className="flex flex-wrap gap-1.5">
        {tiposChips.map((t) => (
          <ChipOpcion
            key={t}
            activo={tipo === t}
            disabled={isSubmitting}
            // Volver a tocar el chip activo lo deselecciona: sin esto no hay
            // forma de dejar el campo vacío una vez elegido.
            onClick={() => elegirTipo(t)}
          >
            {t}
          </ChipOpcion>
        ))}
      </div>
    </div>
  );

  const campoTipoTexto = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-tipo`}>
        Tipo <span className="text-destructive">*</span>
      </Label>
      <Input
        id={`${uid}-tipo`}
        value={tipo}
        onChange={(e) => setTipo(e.target.value)}
        placeholder="Excavadora, Grúa horquilla…"
        disabled={isSubmitting}
        required
        list={`${uid}-tipos`}
      />
      <datalist id={`${uid}-tipos`}>
        {TIPOS_SUGERIDOS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
    </div>
  );

  const campoMarca = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-marca`}>
        Marca <span className="text-destructive">*</span>
      </Label>
      <Input
        id={`${uid}-marca`}
        value={marca}
        onChange={(e) => setMarca(e.target.value)}
        placeholder="Caterpillar"
        disabled={isSubmitting}
        required
        list={`${uid}-marcas`}
      />
      <datalist id={`${uid}-marcas`}>
        {MARCAS_SUGERIDAS.map((m) => (
          <option key={m} value={m} />
        ))}
      </datalist>
    </div>
  );

  const campoModelo = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-modelo`}>
        Modelo <span className="text-destructive">*</span>
      </Label>
      <Input
        id={`${uid}-modelo`}
        value={modelo}
        onChange={(e) => setModelo(e.target.value)}
        placeholder="320D"
        disabled={isSubmitting}
        required
      />
    </div>
  );

  const campoSerie = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-serie`}>N° de serie</Label>
      <Input
        id={`${uid}-serie`}
        value={serie}
        onChange={(e) => setSerie(e.target.value.toUpperCase())}
        placeholder="CAT-320D-0001"
        disabled={isSubmitting}
        className={cn("fc-num", dupSerie && "border-warning-bar")}
      />
      {/* Aviso, NO bloqueo: la API no exige la serie única, así que cortar el
          guardado acá impediría cargas legítimas (dos equipos gemelos con la
          serie mal transcrita por el proveedor). Sólo se avisa. */}
      {dupSerie && (
        <p className="text-warning flex items-center gap-1.5 text-[11.5px]">
          <Icon name="error" size={15} />
          Ese número de serie ya está registrado en otra máquina.
        </p>
      )}
    </div>
  );

  const campoAnio = (
    <div className="space-y-1.5">
      <Label htmlFor={`${uid}-anio`}>Año</Label>
      <Input
        id={`${uid}-anio`}
        inputMode="numeric"
        value={anio}
        onChange={(e) => setAnio(e.target.value.replace(/\D/g, "").slice(0, 4))}
        placeholder="2026"
        disabled={isSubmitting}
        className="fc-num w-[92px] shrink-0"
      />
    </div>
  );

  const campoAreas = (
    <div className="space-y-1.5">
      <Label>Áreas donde opera</Label>
      <MultiSelect
        options={areasOptions}
        value={areasIds}
        onChange={setAreasIds}
        placeholder={
          isLoadingAreas
            ? "Cargando áreas…"
            : areasOptions.length === 0
              ? "No hay áreas creadas todavía"
              : "Selecciona áreas"
        }
        emptyText="Sin áreas que coincidan."
        disabled={isSubmitting || isLoadingAreas || areasOptions.length === 0}
      />
      {/* Las áreas son un clasificador OPCIONAL: el texto explica qué pasa si
          se dejan vacías, en vez de sugerir que son obligatorias. */}
      <p className="text-soft text-[11.5px] leading-[1.5]">
        {areasIds.length > 0
          ? "Sólo quienes trabajan en esas áreas la verán al hacer un checklist."
          : "Sin área queda visible para toda la empresa al hacer un checklist."}
      </p>
    </div>
  );

  // ── Layouts ───────────────────────────────────────────────────────────────

  if (esSecciones) {
    return (
      <form onSubmit={submit} className={formClassName ?? "space-y-3"}>
        <section className="bg-card flex flex-col gap-3.5 rounded-xl border px-4 py-4">
          <span className="fc-section-title">Identificación</span>
          {/* auto-fit para que envuelva en vez de comprimirse (regla de
              grillas del DS), igual que el prototipo. */}
          <div
            className="grid gap-x-4 gap-y-3"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
          >
            {campoNombre}
            {campoCodigo}
            {campoTipoTexto}
            {campoMarca}
            {campoModelo}
            {campoAnio}
            {campoSerie}
            {campoAreas}
          </div>

          {/* Guardar va DENTRO de la tarjeta y no suelto al final: ahí se ve
              sin buscarlo y se entiende qué guarda — lo que está justo arriba.
              Es el lugar del prototipo y la misma convención que
              `TrabajadorForm` y `VehiculoForm`.

              Fue el motivo original de sacar `activo` de acá: un interruptor
              POR DEBAJO del botón que lo guarda promete algo que no cumple.
              Hoy `activo` tampoco está en el alta — vive sólo en la Ficha. */}
          {renderFooter({ isSubmitting, resumen })}
        </section>
      </form>
    );
  }

  return (
    <form onSubmit={submit} className={formClassName ?? "space-y-0"}>
      {/* El tipo va PRIMERO porque de él sale el código sugerido: elegir el
          chip completa el campo de abajo. Los pasos de la guía siguen este
          mismo orden. */}
      <div className="border-border-subtle flex flex-col gap-3.5 border-b px-1 pb-4">
        <div data-tour="maquinaria-tipo">{campoTipoChips}</div>
        <div className="grid gap-3 sm:grid-cols-[1.35fr_1fr]" data-tour="maquinaria-nombre">
          {campoNombre}
          {campoCodigo}
        </div>
        <div className="grid gap-3 sm:grid-cols-2" data-tour="maquinaria-marca-modelo">
          {campoMarca}
          {campoModelo}
        </div>
        {campoSerie}
        {campoAnio}
      </div>

      {/* Sin `border-b`: es el último bloque del cuerpo y el pie del drawer ya
          trae su propio `border-t`. Con los dos quedaba un divisor huérfano. */}
      <div className="px-1 py-4">{campoAreas}</div>

      {renderFooter({ isSubmitting, resumen })}
    </form>
  );
}
